import { useRef, useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";
import { type CorporationListState } from "./useCorporationList";
import { buttonSx, mock, Panel, PanelActions } from "./Panel";

interface Props {
  state: CorporationListState;
  /** 閲覧専用・処理中・受付設定の未取得などで、ファイルを受け付けない */
  uploadDisabled: boolean;
  onNext: () => void;
  onTrySample: () => void;
  onDownloadSample: (fileType: "csv" | "xlsx") => void;
}

const formatSize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.ceil(bytes / 1024)).toLocaleString()} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/** ① ファイルアップロード。ドラッグ＆ドロップとファイル選択の両方で受け付ける */
export const UploadStep = ({
  state,
  uploadDisabled,
  onNext,
  onTrySample,
  onDownloadSample,
}: Props): JSX.Element => {
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // SPではタップ領域を44px以上にする（レスポンシブ基盤ガイド）
  const tapSx = useIsMobile() ? { minHeight: MIN_TAP_TARGET } : undefined;
  const extension = state.filename.split(".").pop()?.toUpperCase() ?? "";
  const specs = [
    "対応形式 .csv / .xlsx",
    "1ファイル 10MBまで",
    `最大${(state.settings?.maxRows ?? 1000).toLocaleString()}行`,
  ];
  return (
    <Panel
      title="CSV・Excelファイルをアップロード"
      note="先頭行をヘッダーとして読み込みます。アップロード後に使用する列を選択できます。"
      actions={
        <Button
          variant="outlined"
          disabled={uploadDisabled}
          onClick={onTrySample}
          sx={[buttonSx.base, buttonSx.secondary, buttonSx.small, tapSx ?? {}]}
        >
          サンプルファイルで確認
        </Button>
      }
    >
      <Box
        role="group"
        aria-label="法人リストファイルのドロップ先"
        onDragOver={(event) => {
          event.preventDefault();
          if (!uploadDisabled) setDragging(true);
        }}
        onDragLeave={(event) => {
          // 枠内の子要素へ移っただけのdragleaveで表示を戻すと、ドラッグ中に点滅する
          if (!event.currentTarget.contains(event.relatedTarget as Node | null))
            setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (file && !uploadDisabled) void state.upload(file);
        }}
        sx={{
          height: { xs: 200, [MOBILE_BREAKPOINT]: 244 },
          border: "2px dashed",
          borderColor: dragging ? mock.teal : "#b9c8cd",
          borderRadius: "3px",
          bgcolor: dragging ? "#f3fafb" : "#fbfdfd",
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          transition: ".2s",
          "&:hover": { borderColor: mock.teal, bgcolor: "#f3fafb" },
        }}
      >
        <Box>
          <Box
            aria-hidden
            sx={{
              width: 58,
              height: 58,
              mx: "auto",
              mb: "15px",
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              bgcolor: mock.tealSoft,
              color: mock.teal,
              fontSize: 27,
            }}
          >
            ⇧
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
            ファイルをここにドラッグ＆ドロップ
          </Typography>
          <Typography sx={{ color: "#828a8e", fontSize: 13 }}>
            または
          </Typography>
          {/* labelで包むとクリックでしか開かず、Enter・Spaceで選べないため、ボタンから隠したinputを開く */}
          <Button
            variant="outlined"
            disabled={uploadDisabled}
            onClick={() => {
              fileInput.current?.click();
            }}
            sx={[buttonSx.base, buttonSx.outline, { mt: "13px" }, tapSx ?? {}]}
          >
            ファイルを選択
          </Button>
          <input
            ref={fileInput}
            hidden
            type="file"
            accept=".csv,.xlsx"
            aria-label="法人リストファイル"
            disabled={uploadDisabled}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void state.upload(file);
              event.target.value = "";
            }}
          />
        </Box>
      </Box>
      {state.filename && (
        <Stack
          direction="row"
          alignItems="center"
          gap="12px"
          sx={{
            mt: "18px",
            border: "1px solid #c9dfe3",
            bgcolor: "#f5fbfc",
            px: "17px",
            py: "14px",
          }}
        >
          <Box
            sx={{
              width: 39,
              height: 39,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              borderRadius: "3px",
              bgcolor: "#e5f5ee",
              color: "#24735d",
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            {extension}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }} noWrap>
              {state.filename}
            </Typography>
            <Typography sx={{ color: mock.muted, fontSize: 12, mt: "3px" }}>
              {state.file
                ? `${state.file.rows.length.toLocaleString()}行・`
                : ""}
              {formatSize(state.fileSize)}
            </Typography>
          </Box>
          <IconButton
            aria-label="選択したファイルを取り消す"
            onClick={state.clearFile}
            sx={{
              width: MIN_TAP_TARGET,
              height: MIN_TAP_TARGET,
              color: "#8d9699",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      )}
      <Stack
        direction="row"
        justifyContent="center"
        flexWrap="wrap"
        gap={{ xs: "8px 20px", [MOBILE_BREAKPOINT]: "8px 30px" }}
        sx={{ mt: "21px", color: mock.muted, fontSize: 12 }}
      >
        {specs.map((spec) => (
          <Box component="span" key={spec}>
            <Box
              component="span"
              aria-hidden
              sx={{ color: mock.teal, fontWeight: 800, mr: "7px" }}
            >
              ✓
            </Box>
            {spec}
          </Box>
        ))}
      </Stack>
      <Typography
        sx={{ mt: 1, color: mock.muted, fontSize: 12, textAlign: "center" }}
      >
        Excelは先頭シートのみ。CSVはUTF-8・CP932に対応。番号の先頭に0がある場合は文字列で入力してください
      </Typography>
      <Stack
        direction="row"
        justifyContent="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mt: 1 }}
      >
        <Button
          size="small"
          sx={[buttonSx.link, tapSx ?? {}]}
          onClick={() => {
            onDownloadSample("csv");
          }}
        >
          CSVサンプルをダウンロード
        </Button>
        <Button
          size="small"
          sx={[buttonSx.link, tapSx ?? {}]}
          onClick={() => {
            onDownloadSample("xlsx");
          }}
        >
          Excelサンプルをダウンロード
        </Button>
      </Stack>
      <PanelActions>
        <Button
          variant="contained"
          disabled={!state.file || state.busy}
          onClick={onNext}
          sx={[buttonSx.base, buttonSx.primary]}
        >
          次へ：列を設定する
        </Button>
      </PanelActions>
    </Panel>
  );
};
